import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import Loader from 'react-loader-spinner';
import styled, { css } from 'styled-components';
import { useDraft2 } from '../../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { SsoConfig } from '../../../../api/ApiTypes';
import { Button, Colors, Input, remInPx } from '../../../../ui';
import { ResultInfo } from '../../../../ui/ResultInfo';
import { AppNavigator } from '../../../AppNavigator';
import { useResolve } from '../../../Container';
import { ButtonStrip, Copy, Flexer, Title } from '../../../dashboard/elements/LayoutElements';
import { asyncOptionsPopupWith } from '../../../popups/OptionsPopup';
import { PopupManager } from '../../../popups/PopupManager';

const restrictHelpText = `
    When this is enabled, members of the account will be prevented from logging in with any method other than the configured SSO identity provider.
`;

const confirmResetPopup = (execute: any) => asyncOptionsPopupWith(
    'Reset SSO Configuration',
    'Please confirm you want to reset your SSO configuration?  ' +
    'This will prevent your team from using Single Sign-On until it has been reconfigured.',
    [
        ['Reset', 'primary', execute],
    ]
);

const TextArea = styled.textarea<{ error?: boolean }>`
    display: block;
    position: relative;
    width: 100%;
    border-radius: 0.375rem;
    appearance: none;
    background-color: #ffffff;
    border-color: #d2d6dc;
    border-width: 1px;
    border-radius: 0.375rem;
    padding-top: 0.5rem;
    padding-right: 0.75rem;
    padding-bottom: 0.5rem;
    padding-left: 0.75rem;
    font-size: 1rem;
    line-height: 1.5;

    ${p => !p.error ? '' : css`
        border-color: ${Colors.negative};
    `}
`;

const ManageSingleSignOnForm = React.memo<{ data: SsoConfig }>(({ data }) => {

    const nav = useResolve(AppNavigator);
    const popups = useResolve(PopupManager);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    const { draft, draftDirty, patchDraft, resetDraft } = useDraft2(data);
    const [errors, setErrors] = useState({} as Record<string, boolean>);

    useEffect(() => resetDraft(data), [data]);

    const checkErrors = useCallback(() => {
        const results = [
            ['entryPoint', !draft.entryPoint?.trim()],
            ['issuer', !draft.issuer?.trim()],
            ['certificate', !draft.certificate?.trim()],
        ];
        setErrors(Object.fromEntries(results));
        return !!results.find(x => !!x[1]);
    }, [draft]);

    const save = useMutation({
        mutationKey: ['admin.sso.save'],
        mutationFn: async (body: SsoConfig) => api.exec(v2.admin.sso.save({ ...body, version: 'v2' })),
        onSettled: () => {
            qc.invalidateQueries(['admin.sso.info']);
            resetDraft(draft);
        },
    });

    const reset = useMutation({
        mutationKey: ['admin.sso.reset'],
        mutationFn: async () => api.exec(v2.admin.sso.reset()),
        onSettled: () => {
            qc.invalidateQueries(['admin.sso.info']);
            resetDraft(draft);
        },
    });

    const onSave = useCallback(() => {
        if (checkErrors()) return;
        save.mutate(draft);
    }, [checkErrors, draft, save]);

    const onReset = useCallback(async () => {
        const { actioned } = await popups.invoke(confirmResetPopup(() => {
            reset.mutate();
        }));
        if (actioned) {
            nav.go('~/manage/sso/setup');
        }
    }, []);

    return <Flexer flex="column" gap="2" maxWidth="64" padding="1.75 0">
        <Copy flush>
            Ludi supports Single Sign-On (SSO) via the SAML protocol.
            You will need the SAML Endpoint, Issuer and Certificate values from your Identity Provider in order to configure Ludi SSO.
            You will also need to configure the email domains you would like to map for this SSO configuration.  When signing-in, users presenting an
            email address from one of these domains will be redirected to your Identity Provider for authentication.
        </Copy>

        <Flexer flex="column" gap="1">
            <Title color={Colors.text}>Identity Provider Settings</Title>
            <Input label="Entry Point" placeholder="e.g. https://example.okta.com/app/96034485_example/exkcqjdk2RClOiu/sso/saml"
                error={errors.entryPoint}
                message={errors.entryPoint ? 'Entry Point is required.' : ''}
                value={draft.entryPoint} onChange={x => patchDraft({ entryPoint: x })} />
            <Input label="Issuer" placeholder="e.g. http://www.okta.com/exkcqjdk2XGlOiuJm5d6"
                error={errors.issuer}
                message={errors.issuer ? 'Issuer is required.' : ''}
                value={draft.issuer} onChange={x => patchDraft({ issuer: x })} />
            <Input
                multiline={{ rows: 5 }}
                label="Certificate"
                value={draft.certificate}
                error={errors.certificate}
                message={errors.certificate ? 'Certificate is required.' : ''}
                onChange={x => patchDraft({ certificate: x })} />
            <Flexer>
                <Input.Label>Domains</Input.Label>
                <TextArea disabled rows={3} value={draft.domains} onChange={() => { }} />
                <Input.Hint>This setting must be configured by a member of our support team.</Input.Hint>
            </Flexer>
        </Flexer>

        <Flexer flex="column" gap="1">
            <Title color={Colors.text}>Additional Settings</Title>
            <Input
                label="Max Session Duration (Hours)"
                hint="(Optional) How long in hours before each user needs to re-authenticate with the IdP."
                type="number"
                value={`${draft.maxSessionDuration || ''}`}
                error={errors.maxSessionDuration}
                message={errors.maxSessionDuration ? 'Invalid number.' : ''}
                onChange={x => patchDraft({ maxSessionDuration: (!!x ? parseInt(x) : null) || null })}
                placeholder="e.g. 8"
            />
            <Input
                label="Whitelist"
                hint="(Optional) Comma-separated list of email addresses that are allowed to bypass SSO."
                value={draft.whitelist || ''}
                onChange={x => patchDraft({ whitelist: x })}
                placeholder="e.g. *@ourclient.net, john@otherclient.com"
            />
        </Flexer>

        <ButtonStrip>
            <Button disabled={!draftDirty || save.isLoading} variant="primary" onAction={onSave}>
                {save.isLoading ? 'Saving...' : 'Save Configuration'}
                {save.isLoading && <Loader
                    type="TailSpin"
                    color="#fff"
                    height={remInPx(1.2)}
                    width={remInPx(1.2)}
                />}
            </Button>
            <Button disabled={save.isLoading} onAction={onReset}>
                Reset
            </Button>
        </ButtonStrip>
        {save.isSuccess && <ResultInfo mode="positive" message="Access control configuration updated." />}
        {save.isError && <ResultInfo mode="negative" message="Access control configuration failed to update.  Please contact support." />}
    </Flexer>;
});

export default ManageSingleSignOnForm;
